import React, { Children } from 'react';
import { motion } from 'framer-motion';
const ACCENT = '#2563EB';
const FONT = '"Poppins", "Inter", system-ui, sans-serif';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
type SectionProps = {
  title: string;
  children: React.ReactNode;
};
function Section({
  title,
  children
}: SectionProps) {
  return <motion.section variants={fadeUp} className="mb-12" data-id="element-3190" data-mp="zw2bj3r6qfu">
      <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-5 leading-[1.2]" data-id="element-3191" data-mp="11fi78lhyzh3">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] text-[#334155] leading-relaxed" data-id="element-3192" data-mp="34ofud0o9v4ml">
        {children}
      </div>
    </motion.section>;
}
export function Terms() {
  const lastUpdated = 'May 2026';
  return <div className="w-full min-h-screen bg-white text-[#1E293B]" style={{
    fontFamily: FONT
  }} data-id="element-3193" data-mp="qoah91rt3h89">
      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 border-b border-[#EDF1F7]" data-id="element-3194" data-mp="vm2jopq2ulqp">
        <div className="max-w-3xl mx-auto" data-id="element-3195" data-mp="10feu9vxx8s0g">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-3196" data-mp="15d6z1qjo08m2">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-3197" data-mp="15f9n4519bro5">
              Legal
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#0F172A] leading-[1.05] mb-5" data-id="element-3198" data-mp="hmjcq0905gfz">
              Terms of Service
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm text-[#94A3B8]" data-id="element-3199" data-mp="1t4u78r95cuz0">
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-20 px-6 md:px-10" data-id="element-3200" data-mp="3u6tse3vrik7x">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-3201" data-mp="3p54emgh5nsg5">
          <motion.p variants={fadeUp} className="text-[15px] text-[#334155] leading-relaxed mb-12" data-id="element-3202" data-mp="3agzsf2iw8ysm">
            These Terms of Service ("Terms") govern your access to and use of
            the Structured Nutrition website, products, and services
            ("Services"). By using our Services, you agree to these Terms. If
            you do not agree, please do not use the Services.
          </motion.p>

          {/* DISCLAIMERS — two clearly separated callouts */}
          <motion.div variants={fadeUp} className="mb-6 p-6 md:p-8 border-l-4 rounded-r-2xl" style={{
          backgroundColor: '#F5F8FF',
          borderColor: ACCENT
        }} data-id="element-3203" data-mp="2mroz263vi6oi">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: ACCENT
          }} data-id="element-3204" data-mp="170wh92obcj8b">
              General Content — Educational Only
            </p>
            <p className="text-[15px] text-[#334155] leading-relaxed" data-id="element-3205" data-mp="1lxdtdw4bu1vx">
              The general content published on this website, in our books,
              guides, downloads, emails, and social media is for educational and
              informational purposes only. Because we don't know your individual
              medical history, medications, or circumstances, this content is
              not personalized nutrition advice and is not a substitute for
              consulting a licensed healthcare provider. Always speak with your
              prescribing physician before making changes to your medication,
              nutrition, or exercise plan — especially while taking GLP-1
              medications. Never disregard professional medical advice or delay
              seeking it because of something you read here.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="mb-12 p-6 md:p-8 border-l-4 rounded-r-2xl" style={{
          backgroundColor: '#F5F8FF',
          borderColor: ACCENT
        }} data-id="element-3206" data-mp="c0p20trs5lie">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: ACCENT
          }} data-id="element-3207" data-mp="zpi314n9ejjv">
              1:1 Coaching — Professional Dietetic Services
            </p>
            <p className="text-[15px] text-[#334155] leading-relaxed" data-id="element-3208" data-mp="prlqc8eobgjn">
              One-on-one nutrition coaching with Structured Nutrition is
              provided by a Registered Dietitian Nutritionist (RDN) and consists
              of medical nutrition therapy and individualized nutrition
              counseling within the scope of practice of a credentialed
              dietitian. These services complement, but do not replace, the care
              of your prescribing physician or other healthcare providers. We do
              not diagnose disease, prescribe medication, or adjust prescription
              dosing — those decisions remain with your physician. Clients are
              responsible for keeping their medical team informed about the
              nutrition plan we develop together.
            </p>
          </motion.div>

          <Section title="Our Services" data-id="element-3209" data-mp="3t2jw6m1agfi2">
            <p data-id="element-3210" data-mp="3t3l9yy9ovg3h">
              Structured Nutrition offers nutrition coaching, digital products
              (e-books, downloadable guides), training programs, and related
              educational content. Service descriptions, availability, and
              pricing may change without notice.
            </p>
          </Section>

          <Section title="Accounts" data-id="element-3211" data-mp="1vmxn4kg652dh">
            <p data-id="element-3212" data-mp="1vnyykq13jqvq">
              To access certain features, you'll need to create an account. You
              agree to:
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-3213" data-mp="1qq7ak73koowt">
              <li data-id="element-3214" data-mp="25m4yj23cemxy">Provide accurate and complete information</li>
              <li data-id="element-3215" data-mp="20lr26hb3t1fr">Keep your login credentials confidential</li>
              <li data-id="element-3216" data-mp="20odaij5tjkz1">
                Be responsible for all activity that happens under your account
              </li>
              <li data-id="element-3217" data-mp="29mylnt8ynxps">
                Notify us immediately of any unauthorized use of your account
              </li>
            </ul>
            <p data-id="element-3218" data-mp="2oc4l9ibpnpy7">
              We may suspend or terminate accounts that violate these Terms.
            </p>
          </Section>

          <Section title="Purchases and Payment" data-id="element-3219" data-mp="2jda38ilki5f5">
            <p data-id="element-3220" data-mp="2xc9pjjlly885">
              Payments are processed securely by Stripe. By purchasing a product
              or service, you authorize us to charge the payment method you
              provide for the full purchase amount, including any applicable
              taxes.
            </p>
            <p data-id="element-3221" data-mp="3gycmnv86ki8g">
              All prices are listed in U.S. dollars unless otherwise noted. We
              reserve the right to change pricing at any time.
            </p>
          </Section>

          <Section title="Refunds" data-id="element-3222" data-mp="37xmxhadfdq3p">
            <p data-id="element-3223" data-mp="37wlkv1grgvjv">
              Digital products (e-books, guides, downloads) are non-refundable
              once accessed or downloaded. Coaching services may be eligible for
              a refund within 7 days of purchase if no session has yet been
              delivered — contact us to request one. Refunds for ongoing
              programs are evaluated case-by-case.
            </p>
          </Section>

          <Section title="Intellectual Property" data-id="element-3224" data-mp="2oagv14xgxgij">
            <p data-id="element-3225" data-mp="2a9wao4bwncmx">
              All content on this site — including text, graphics, logos, books,
              guides, and training materials — is owned by Structured Nutrition
              or licensed to us and is protected by copyright and other
              intellectual property laws.
            </p>
            <p data-id="element-3226" data-mp="2ps5pl1w475ie">
              You may not copy, distribute, resell, or create derivative works
              from our content without our prior written permission. Purchasing
              a digital product grants you a personal, non-transferable license
              to use it.
            </p>
          </Section>

          <Section title="Acceptable Use" data-id="element-3227" data-mp="2bseg9egjmdaq">
            <p data-id="element-3228" data-mp="2btfu1qoy1dw5">By using our Services, you agree NOT to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-3229" data-mp="27164ibrqqfue">
              <li data-id="element-3230" data-mp="222bf2qz9pt1b">
                Use the Services for any unlawful, harmful, or fraudulent
                purpose
              </li>
              <li data-id="element-3231" data-mp="1y1rhjmas8nen">
                Share, redistribute, or resell our digital products or coaching
                materials
              </li>
              <li data-id="element-3232" data-mp="1t3pwivtsqe0j">
                Attempt to gain unauthorized access to our systems or other
                users' accounts
              </li>
              <li data-id="element-3233" data-mp="3layfhn1k1aq">Harass, abuse, or threaten our team or other users</li>
              <li data-id="element-3234" data-mp="8jm6pnxou3qu">
                Use our content to provide nutrition or medical advice to others
                as a substitute for your own credentialed practice
              </li>
            </ul>
          </Section>

          <Section title="Disclaimer of Warranties" data-id="element-3235" data-mp="hkke8g7c4te6">
            <p data-id="element-3236" data-mp="hi6j67cnnh6m">
              Our Services are provided "as is" and "as available," without
              warranties of any kind, either express or implied. We do not
              guarantee any specific health, weight, or fitness outcomes from
              using our Services.
            </p>
          </Section>

          <Section title="Limitation of Liability" data-id="element-3237" data-mp="3uqstwg4twili">
            <p data-id="element-3238" data-mp="3urm3rpz33o7v">
              To the fullest extent permitted by law, Structured Nutrition and
              its team are not liable for any indirect, incidental, special,
              consequential, or punitive damages arising from your use of the
              Services — including but not limited to health outcomes, lost
              profits, or data loss.
            </p>
            <p data-id="element-3239" data-mp="1aun1rv41ds4r">
              Our total liability for any claim arising from these Terms or your
              use of the Services is limited to the amount you paid us in the 12
              months preceding the event giving rise to the claim.
            </p>
          </Section>

          <Section title="Indemnification" data-id="element-3240" data-mp="n63v1k0mxnes">
            <p data-id="element-3241" data-mp="n7exngicgp4b">
              You agree to defend, indemnify, and hold harmless Structured
              Nutrition from any claims, damages, or expenses (including
              reasonable attorneys' fees) arising out of your violation of these
              Terms or your misuse of the Services.
            </p>
          </Section>

          <Section title="Governing Law" data-id="element-3242" data-mp="10z3bppc5lnnx">
            <p data-id="element-3243" data-mp="3gtfq74sx4h7">
              These Terms are governed by the laws of the State of [Your State],
              without regard to conflict-of-laws principles. Any dispute will be
              resolved in the state or federal courts located in [Your
              County/State].
            </p>
          </Section>

          <Section title="Changes to These Terms" data-id="element-3244" data-mp="3s515yohnco0x">
            <p data-id="element-3245" data-mp="3n4mzhavp1m50">
              We may update these Terms from time to time. The "Last updated"
              date at the top reflects the most recent revision. Continued use
              of the Services after changes constitutes acceptance of the
              updated Terms.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-3246" data-mp="39cq8xb9obkaq">
            <p data-id="element-3247" data-mp="39bnjl7s01pwx">
              Questions about these Terms? Reach out through our{' '}
              <a href="/contact" className="underline hover:no-underline" style={{
              color: ACCENT
            }} data-id="element-3248" data-mp="34ex3wlk3wwc1">
                contact page
              </a>
              .
            </p>
          </Section>

          <motion.p variants={fadeUp} className="text-xs text-[#94A3B8] italic mt-16 pt-8 border-t border-[#EDF1F7]" data-id="element-3249" data-mp="2qffd60n93901">
            This document is provided as a general template and does not
            constitute legal advice. Bracketed placeholders (such as state and
            venue) should be filled in, and the full document should be reviewed
            with an attorney before being relied upon.
          </motion.p>
        </motion.div>
      </section>
    </div>;
}